import React, { useState, useEffect } from "react";
import {
	Datagrid,
	List,
	TextField,
	DateField,
	useRecordContext,
	FunctionField,
	Button,
	BooleanField,
	useDataProvider,
	useRefresh,
	useNotify,
} from "react-admin";
import { Box, SnackbarContent } from "@mui/material";
import { DocumentIcon } from "@logora/parliament.icons.regular_icons";
import { AmendmentIcon } from "@logora/parliament.icons.regular_icons";
import { DossierIcon } from "@logora/parliament.icons.regular_icons";
import { SpeechIcon } from "@logora/parliament.icons.regular_icons";
import { OrganeIcon } from "@logora/parliament.icons.regular_icons";
import { QuestionIcon } from "@logora/parliament.icons.regular_icons";
import { ReunionIcon } from "@logora/parliament.icons.regular_icons";
import { VoteIcon } from "@logora/parliament.icons.regular_icons";
import { ActeurIcon } from "@logora/parliament.icons.regular_icons";
import { Label } from "@logora/parliament.ui.label";
import { useModal } from "@logora/debate.dialog.modal";
import { AlertModal } from "./AlertModal";
import { AlertListEmpty } from "./AlertListEmpty";
import { AlertIcon } from "@logora/parliament.icons.regular_icons";
import Tabs from "../components/Tabs";
import AlertSourceField from "./AlertSourceField";
import SettingsIcon from "@mui/icons-material/Settings";
import Switch from "@mui/material/Switch";
import styles from "./AlertList.module.scss";
import rowStyle from "../../debate/arguments/rowStyle";

export const AlertList = () => {
	const { showModal } = useModal();
	const dataProvider = useDataProvider();
	const [tabCounts, setTabCounts] = useState({ keywords: 0, dossiers: 0 });

	const fetchTabCounts = async () => {
		try {
			const { total: keywordsCount } = await dataProvider.getList("alerts", {
				pagination: { page: 1, perPage: 1000 },
				sort: { field: "created_at", order: "DESC" },
				filter: { no_target: "true" },
			});
			const { total: dossiersCount } = await dataProvider.getList("alerts", {
				pagination: { page: 1, perPage: 1000 },
				sort: { field: "created_at", order: "DESC" },
				filter: { target_class: "Dossier" },
			});
			const { total: acteursCount } = await dataProvider.getList("alerts", {
				pagination: { page: 1, perPage: 1000 },
				sort: { field: "created_at", order: "DESC" },
				filter: { target_class: "Acteur" },
			});
			const { total: questionsCount } = await dataProvider.getList("alerts", {
				pagination: { page: 1, perPage: 1000 },
				sort: { field: "created_at", order: "DESC" },
				filter: { target_class: "Question" },
			});
			const { total: amendementsCount } = await dataProvider.getList("alerts", {
				pagination: { page: 1, perPage: 1000 },
				sort: { field: "created_at", order: "DESC" },
				filter: { target_class: "Amendement" },
			});
			const { total: articlesCount } = await dataProvider.getList("alerts", {
				pagination: { page: 1, perPage: 1000 },
				sort: { field: "created_at", order: "DESC" },
				filter: { target_class: "Subdivision" },
			});
			const { total: reunionsCount } = await dataProvider.getList("alerts", {
				pagination: { page: 1, perPage: 1000 },
				sort: { field: "created_at", order: "DESC" },
				filter: { target_class: "Reunion" },
			});
			setTabCounts({
				keywords: keywordsCount,
				dossiers: dossiersCount,
				acteurs: acteursCount,
				questions: questionsCount,
				amendements: amendementsCount,
				reunions: reunionsCount,
				articles: articlesCount,
			});
		} catch (error) {
			console.error("Error fetching tab counts:", error);
		}
	};

	useEffect(() => {
		fetchTabCounts();
	}, [dataProvider]);

	const generateTabContent = (filter, icon, label, count) => ({
		label: (
			<span className={styles.tabItem}>
				{icon} {label} ({count})
			</span>
		),
		content: (
			<Box display="flex">
				<List
					className={styles.listWrapper}
					resource="alerts"
					actions={null}
					sort={{ field: "created_at", order: "DESC" }}
					filter={filter}
					disableSyncWithLocation
					component="div"
					empty={<AlertListEmpty onCreateAlert={() => fetchTabCounts()} />}
					title={false}
				>
					<Datagrid
						bulkActionButtons={null}
						rowClick={(id, resource, record) =>
							record?.target_url
								? new URL(record.target_url).hash.slice(
										1,
										record.target_url.length - 1,
									)
								: null
						}
						size="small"
						rowStyle={rowStyle}
					>
						<DateField
							label="Créé le"
							source="created_at"
							options={{
								year: "numeric",
								month: "numeric",
								day: "numeric",
							}}
						/>
						<TextField label="Nom" source="name" />
						<FunctionField
							label="Mots-clés"
							render={(record) => (
								<div className={styles.keywords}>
									{record.keywords.length === 0 && (
										<Label key={"noKeywords"} text={"Aucun mot-clé"} />
									)}
									{record.keywords.slice(0, 4).map((k) => (
										<Label key={k.name} text={k.name} />
									))}
									{record.keywords.length > 4 && (
										<Label
											key={"moreKeywords"}
											text={`+ ${record.keywords.length - 4}`}
										/>
									)}
								</div>
							)}
						/>

						{filter.target_class && (
							<SourceTargetField source="Source principale" />
						)}
						{filter.no_target && (
							<FunctionField
								label="Sources"
								render={(record) => <SourceField record={record} />}
							/>
						)}
						<FunctionField
							label="Fréquence"
							render={(record) => {
								switch (record.frequency) {
									case "hourly":
										return <Label text="Horaire" />;
									case "daily":
										return <Label text="Quotidien" />;
									case "weekly":
										return <Label text="Hebdomadaire" />;
									case "monthly":
										return <Label text="Mensuel" />;
									default:
										return "";
								}
							}}
						/>
						{filter.target_class && (
							<BooleanField
								source={"is_sent"}
								FalseIcon={null}
								style={{ color: "#4CAF50" }}
								label="E-mail reçu"
							/>
						)}
						<FunctionField
							label="Active"
							render={(record) => <SwitchBooleanField record={record} />}
						/>
						<FunctionField
							label="Actions"
							render={(record) => <UpdateButton record={record} />}
						/>
					</Datagrid>
				</List>
			</Box>
		),
	});

	return (
		<>
			<Button
				className={styles.topButton}
				label="Ajouter une alerte"
				onClick={() =>
					showModal(<AlertModal onCreateAlert={() => fetchTabCounts()} />)
				}
			/>
			<Tabs
				tabData={[
					generateTabContent(
						{ no_target: "true" },
						<AlertIcon width={18} height={18} style={{ marginRight: "5px" }} />,
						"Mots-clés",
						tabCounts.keywords,
					),
					generateTabContent(
						{ target_class: "Dossier" },
						<DossierIcon
							width={18}
							height={18}
							style={{ marginRight: "5px" }}
						/>,
						"Dossiers",
						tabCounts.dossiers,
					),
					generateTabContent(
						{ target_class: "Acteur" },
						<ActeurIcon
							width={18}
							height={18}
							style={{ marginRight: "5px" }}
						/>,
						"Acteurs",
						tabCounts.acteurs,
					),
					generateTabContent(
						{ target_class: "Question" },
						<QuestionIcon
							width={18}
							height={18}
							style={{ marginRight: "5px" }}
						/>,
						"Questions",
						tabCounts.questions,
					),
					generateTabContent(
						{ target_class: "Amendement" },
						<AmendmentIcon
							width={18}
							height={18}
							style={{ marginRight: "5px" }}
						/>,
						"Amendements",
						tabCounts.amendements,
					),
					generateTabContent(
						{ target_class: "Reunion" },
						<ReunionIcon
							width={18}
							height={18}
							style={{ marginRight: "5px" }}
						/>,
						"Réunions",
						tabCounts.reunions,
					),
					generateTabContent(
						{ target_class: "Article" },
						<DocumentIcon
							width={18}
							height={18}
							style={{ marginRight: "5px" }}
						/>,
						"Articles",
						tabCounts.articles,
					),
				]}
			/>
		</>
	);
};

const SwitchBooleanField = () => {
	const record = useRecordContext();
	const notify = useNotify();
	const refresh = useRefresh();
	const dataProvider = useDataProvider();

	if (!record) {
		return null;
	}

	const switchAlert = () => {
		dataProvider
			.update("alerts", {
				id: record.id,
				data: { active: !record.active },
			})
			.then(() => {
				refresh();
				notify(
					<SnackbarContent
						autoHideDuration={3000}
						anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
						message={"Alerte modifiée avec succès"}
						sx={{ backgroundColor: "green", color: "#FFF" }}
					/>,
				);
			})
			.catch(() => {
				console.log("Une erreur est survenue");
			});
	};

	return (
		<Switch
			onClick={(e) => e.stopPropagation()}
			checked={record.active}
			onChange={(e) => {
				switchAlert();
				e.stopPropagation();
			}}
		/>
	);
};

const SourceField = () => {
	const getIcon = (name) => {
		const icons = {
			documents: <DocumentIcon width={18} height={18} />,
			amendements: <AmendmentIcon width={18} height={18} />,
			dossiers: <DossierIcon width={18} height={18} />,
			scrutins: <VoteIcon width={18} height={18} />,
			interventions: <SpeechIcon width={18} height={18} />,
			organes: <OrganeIcon width={18} height={18} />,
			questions: <QuestionIcon width={18} height={18} />,
			reunions: <ReunionIcon width={18} height={18} />,
			votes: <VoteIcon width={18} height={18} />,
			acteurs: <ActeurIcon width={18} height={18} />,
		};
		return icons[name.toLowerCase()] || null;
	};

	const record = useRecordContext();

	if (!record) {
		return null;
	}

	if (record.sources.length === 5) {
		return (
			<div className={styles.keywords}>
				<Label
					key={"allSources"}
					text={"Toutes les sources"}
					leftIcon={<AlertIcon width={18} height={18} />}
				/>
			</div>
		);
	}

	if (record.sources.length >= 0) {
		return (
			<div className={styles.keywords}>
				{record.sources.slice(0, 3).map((s) => (
					<Label
						key={s.name}
						text={s.name.charAt(0).toUpperCase() + s.name.slice(1)}
						leftIcon={getIcon(s.name)}
					/>
				))}
				{record.sources.length > 3 && (
					<Label key={"moreSources"} text={`+ ${record.sources.length - 3}`} />
				)}
			</div>
		);
	}
};

const SourceTargetField = () => {
	const record = useRecordContext();

	if (!record) {
		return null;
	}

	if (record.target_id && record.target_class) {
		return (
			<div className={styles.keywords}>
				<AlertSourceField
					targetId={record.target_id}
					targetClass={record.target_class}
				/>
			</div>
		);
	}
};

const UpdateButton = () => {
	const { showModal } = useModal();
	const record = useRecordContext();

	if (!record) {
		return null;
	}
	return (
		<Button
			className={styles.editButton}
			startIcon={<SettingsIcon />}
			label={null}
			onClick={(e) => {
				e.stopPropagation();
				showModal(<AlertModal record={record} />);
			}}
			record={record}
		/>
	);
};
